export const fileTypeOptions = [
  { option: '1:1', value: 1 },
  // { option: '1:N', value: 2 },
  // { option: 'N:1', value: 3 },
];

export const dataTypeOptions = [
  { option: 'テキスト', value: 'string', description: '会社名、担当者名、住所など' },
  { option: 'テキストエリア', value: 'textarea', description: '契約書の条文、備考欄など長文' },
  { option: '数字', value: 'number', description: '金額、数量、税率など' },
  { option: '日付', value: 'date', description: '請求日、納品日、契約日など' },
  { option: '複数行データ', value: 'array', description: '明細行など' },
  { option: 'ラジオボタン', value: 'radio', description: '区分、有無など選択肢から1つ選ぶ' },
  { option: 'プルダウン', value: 'select', description: '選択肢が多い項目（都道府県、部門など）' },
];

// 日付型の出力形式オプション（デフォルト: YYYY/MM/DD）
export const dateOutputFormatOptions = [
  { option: 'YYYY/MM/DD', value: 'YYYY/MM/DD', example: '2026/03/10' },
  { option: 'YYYY-MM-DD', value: 'YYYY-MM-DD', example: '2026-03-10' },
  { option: 'YYYY.MM.DD', value: 'YYYY.MM.DD', example: '2026.03.10' },
  { option: 'YYYYMMDD', value: 'YYYYMMDD', example: '20260310' },
  { option: 'YYYY年MM月DD日', value: 'YYYY年MM月DD日', example: '2026年03月10日' },
  { option: 'YYYY年M月D日', value: 'YYYY年M月D日', example: '2026年3月10日' },
  { option: 'YYYY/M/D', value: 'YYYY/M/D', example: '2026/3/10' },
  { option: 'YYYY-M-D', value: 'YYYY-M-D', example: '2026-3-10' },
  { option: 'YY/MM/DD', value: 'YY/MM/DD', example: '26/03/10' },
  { option: 'YY-MM-DD', value: 'YY-MM-DD', example: '26-03-10' },
  { option: 'YYMMDD', value: 'YYMMDD', example: '260310' },
  { option: 'MM/DD/YYYY', value: 'MM/DD/YYYY', example: '03/10/2026' },
  { option: 'DD/MM/YYYY', value: 'DD/MM/YYYY', example: '10/03/2026' },
  { option: 'MM/DD', value: 'MM/DD', example: '03/10' },
  { option: 'M/D', value: 'M/D', example: '3/10' },
  { option: 'MM月DD日', value: 'MM月DD日', example: '03月10日' },
  { option: 'M月D日', value: 'M月D日', example: '3月10日' },
];

export const DEFAULT_DATE_OUTPUT_FORMAT = 'YYYY/MM/DD';

/**
 * 日付の出力形式として許容する値かどうかを判定する（#3144）。
 *
 * 出力形式は dayjs の format() にそのまま渡され、さらに OCR プロンプトにも
 * 埋め込まれる。選択肢外の値（例: 'YYYY-MM-DD HH:MM:SS'）が保存されると
 * dayjs のトークンとして解釈されず（MM は月、SS はトークンではない）、
 * 壊れた文字列が OCR 結果・CSV 出力・検索インデックスに残ってしまう。
 */
export const isValidDateOutputFormat = (value: unknown): boolean =>
  typeof value === 'string' && dateOutputFormatOptions.some(opt => opt.value === value);

/**
 * 外部由来の値（JSON 取り込みなど）を、許容される出力形式へ正規化する（#3144）。
 * 選択肢外の不正な値はデフォルトへフォールバックさせる。
 *
 * @param value 正規化したい値
 * @param isDateType 日付型の項目かどうか。true の場合は未指定でもデフォルトを補う。
 *   日付型は出力形式が未指定だと表示・出力の整形が行われず、OCR が返した文字列が
 *   そのまま残ってしまうため、取り込み時点でデフォルトを与えて表示を揃える。
 *   日付型以外に出力形式は不要なので、その場合は未指定のままにする。
 */
export const resolveDateOutputFormat = (value: unknown, isDateType = false): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return isDateType ? DEFAULT_DATE_OUTPUT_FORMAT : undefined;
  }
  return isValidDateOutputFormat(value) ? (value as string) : DEFAULT_DATE_OUTPUT_FORMAT;
};

export const displayWidthOptions = [
  { option: '列幅:大', value: 1 },
  { option: '列幅:中', value: 2 },
  { option: '列幅:小', value: 3 },
];

// ===== 計算フィールド（#3145）=====

/**
 * 計算式入力欄の常時表示ヘルプ。
 * {…} に入るのは項目名ではなくフィールドコード（取り違え防止のため常時表示する。設計書 §7.1）
 */
export const CALCULATION_FORMULA_HELP_TEXT =
  '{ } には項目名ではなくフィールドコードが入ります。「項目を挿入」から選ぶと自動で入力されます。';

/**
 * 計算式の設定例（入力欄の下に常時表示する）。
 * 式だけでは結果が想像しづらいため、前提の項目値と結果もセットで持つ。
 * 表示文言（「設定例）」「→」など）は FormulaEditor 側で組み立てる。
 * 式と結果が食い違わないよう、計算エンジンで評価して一致を検証している
 * （`options.calculationExamples.test.ts`）。
 */
export type CalculationFormulaExample = {
  /** 計算式。{ } の中はフィールドコード */
  formula: string;
  /** 式が参照する項目の値。表示する「単価=1000、数量=3」もこれを元に作る */
  values: Record<string, string>;
  /** values を入れて計算したときの結果 */
  result: string;
};

export const calculationFormulaExamples: CalculationFormulaExample[] = [
  { formula: '{姓} & " " & {名}', values: { 姓: '山田', 名: '太郎' }, result: '山田 太郎' },
  { formula: 'LEFT({商品コード}, 2)', values: { 商品コード: 'AB-1234' }, result: 'AB' },
  { formula: '{単価} * {数量}', values: { 単価: '1000', 数量: '3' }, result: '3000' },
  // 税込金額は頻出だが 1.1 を掛けるだけでは小数が出るため、丸め方まで含めて示す
  { formula: 'ROUNDDOWN({金額} * 1.1, 0)', values: { 金額: '65567' }, result: '72123' },
  {
    formula: 'REPLACE({会社名}, "^(株式会社|㈱)", "")',
    values: { 会社名: '株式会社ハイストーリー' },
    result: 'ハイストーリー',
  },
  // 全角で読み取れたテキストを半角に揃える（#3359）。
  // 数字型は読み取りの時点でほぼ半角になるため、ASC が要るのは主にテキスト型の
  // コード・番号・記号混じりの値（基幹システムが半角を期待するケース）
  {
    formula: 'ASC({商品コード})',
    values: { 商品コード: 'ＡＢ－１２３' },
    result: 'AB-123',
  },
  // 半角に揃えたうえで記号を落とす、という組み合わせも使える（#3359）
  {
    formula: 'REPLACE(ASC({電話番号}), "[^0-9]", "")',
    values: { 電話番号: '０３－１２３４－５６７８' },
    result: '0312345678',
  },
  // 条件分岐（#3358）。判定結果を選択肢型で受けると画面にそのまま表示される
  {
    formula: 'IF({金額} > 10000, "高額", "通常")',
    values: { 金額: '20000' },
    result: '高額',
  },
  // 複数条件（#3358）。AND が無いと IF の入れ子になり読みにくい
  {
    formula: 'IF(AND({入数} = "", {数量} = ""), "要確認", "OK")',
    values: { 入数: '', 数量: '' },
    result: '要確認',
  },
  // 正規表現の後方参照。$1 $2 で「マッチした部分」を置換先に差し込める（#3359）
  {
    formula: 'REPLACE({文書番号}, "([0-9]{4})年([0-9]{2})月", "$1/$2")',
    values: { 文書番号: '2026年09月分' },
    result: '2026/09分',
  },
];

/** 「関数を挿入」プルダウンの選択肢。insertText を計算式のカーソル位置に挿入する */
export const calculationFunctionInsertOptions = [
  { value: 'LEFT', option: 'LEFT（先頭からN文字を取り出す）', insertText: 'LEFT()' },
  { value: 'RIGHT', option: 'RIGHT（末尾からN文字を取り出す）', insertText: 'RIGHT()' },
  { value: 'MID', option: 'MID（指定位置からN文字を取り出す）', insertText: 'MID()' },
  // 置換文字列では $1 $2 でマッチした部分を差し込める（#3359）
  {
    value: 'REPLACE',
    option: 'REPLACE（正規表現で置換する・$1で差し込み）',
    insertText: 'REPLACE()',
  },
  { value: 'MATCH', option: 'MATCH（正規表現で検索する）', insertText: 'MATCH()' },
  // 全角→半角変換（#3359）。カナは変換しない
  { value: 'ASC', option: 'ASC（全角の英数字・記号を半角にそろえる）', insertText: 'ASC()' },
  // 条件分岐・集計（#3358）
  { value: 'IF', option: 'IF（条件で値を出し分ける）', insertText: 'IF()' },
  // 論理演算（#3358）。IF の入れ子にせず複数条件を書けるようにする
  { value: 'AND', option: 'AND（すべての条件を満たす）', insertText: 'AND()' },
  { value: 'OR', option: 'OR（どれかの条件を満たす）', insertText: 'OR()' },
  { value: 'NOT', option: 'NOT（条件を反転する）', insertText: 'NOT()' },
  { value: 'SUM', option: 'SUM（明細の列を合計する）', insertText: 'SUM()' },
  // 丸め関数（#3145）。桁数は Excel と同じ意味（0=整数、1=小数第1位、-2=100の位）
  { value: 'ROUND', option: 'ROUND（四捨五入する）', insertText: 'ROUND()' },
  { value: 'ROUNDDOWN', option: 'ROUNDDOWN（切り捨てる）', insertText: 'ROUNDDOWN()' },
  { value: 'ROUNDUP', option: 'ROUNDUP（切り上げる）', insertText: 'ROUNDUP()' },
];

/** フィールドコード入力欄の説明 */
export const FIELD_CODE_HELP_TEXT =
  '計算式で項目を参照するときの識別子です（初期値は項目名）。項目名を変更しても追随しません。';
